import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { WidgetCategoriesStats, WidgetCategory } from "~/api/Api";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { CATEGORY_AUTOMATION_KIND } from "~/helpers/consts";
import { usePageTitle } from "~/hooks/usePageTitle";

import { apiClient } from "../../../api/ApiClient";
import OnPage from "../../../components/automations/onPage/OnPage";
import styles from "./OnPage.module.scss";

export type WidgetCategoryStat = WidgetCategoriesStats[string];
export type WidgetCategoryStatWithSlug = WidgetCategoryStat & { slug: string };
export type Category = WidgetCategory & WidgetCategoryStat;

// Turn into Array and add slug
export const toStatArray = (
  record: WidgetCategoriesStats | undefined | null,
): WidgetCategoryStatWithSlug[] => {
  if (!record) return [];

  return Object.entries(record).map(([slug, data]) => ({
    slug,
    ...data,
  }));
};

// Merge stats with category data and (optional) filter by kind
export const mergeStatsWithCategories = (
  statItems: WidgetCategoryStatWithSlug[],
  allCategories: WidgetCategory[],
  opts?: { kind?: WidgetCategory["kind"] },
): Category[] => {
  const byId = new Map(allCategories.map((c) => [c.id, c]));

  return statItems
    .map((statItem) => {
      const category = byId.get(statItem.id);
      if (!category) return null;

      return { ...category, ...statItem };
    })
    .filter(
      (c): c is Category => !!c && (opts?.kind ? c.kind === opts.kind : true),
    );
};

const OnPagePage = () => {
  usePageTitle(PAGE_TITLES.onPage);
  const { id } = useParams();

  const { data: allCategories } = useQuery({
    enabled: !!id,
    queryKey: ["onpage-all-categories", id],
    queryFn: async () => {
      const response = await apiClient.widget.categoriesList();

      return response.data?.["widget/categories"];
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["onpage-categories-stats", id],
    enabled: !!id && !!allCategories,
    queryFn: async () => {
      // for ts safety as it ignores "enabled" conditions
      if (!allCategories) return [];

      const response = await apiClient.sites.widgetCategoriesStatsDetail(
        Number(id),
      );

      const resp = response?.data?.["widget/categories_stats"];
      const statCategories = toStatArray(resp).filter(
        (stats) => stats.appearances > 0,
      );

      return mergeStatsWithCategories(statCategories, allCategories, {
        kind: CATEGORY_AUTOMATION_KIND,
      });
    },
  });

  const categories = useMemo<Category[]>(() => data ?? [], [data]);

  const noData = categories?.length === 0 && !isLoading;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>On Page</h1>
      <OnPage categories={categories} isLoading={isLoading} noData={noData} />
    </div>
  );
};
export default OnPagePage;
